import type {SerializedStyles} from '@emotion/react';

import {css} from '@emotion/react';

import useAppTheme from './useAppTheme';

export const HEADING_SIZES = ['xxl', 'xl', 'l', 'm', 's'] as const;
export type HeadingSizeKey = (typeof HEADING_SIZES)[number];
export type HeadingSizes = Record<HeadingSizeKey, SerializedStyles>;

export const TITLE_SIZES = ['xxl', 'xl', 'l', 'm', 's', 'xs', 'xxs'] as const;
export type TitleSizeKey = (typeof TITLE_SIZES)[number];
export type TitleSizes = Record<TitleSizeKey, SerializedStyles>;

export const BODY_SIZES = ['l', 'm', 's'] as const;
export type BodySizeKey = (typeof BODY_SIZES)[number];
export type BodySizes = Record<BodySizeKey, SerializedStyles>;

export const PARAGRAPH_SIZES = ['l', 'm', 's', 'xs'] as const;
export type ParagraphSizeKey = (typeof PARAGRAPH_SIZES)[number];
export type ParagraphSizes = Record<ParagraphSizeKey, SerializedStyles>;

export const LIST_SIZES = ['l', 'm', 's', 'xs'] as const;
export type ListSizeKey = (typeof LIST_SIZES)[number];
export type ListSizes = Record<ListSizeKey, SerializedStyles>;

type UseTypography = {
  body: BodySizes;
  detail: SerializedStyles;
  heading: HeadingSizes;
  link: SerializedStyles;
  list: ListSizes;
  paragraph: ParagraphSizes;
  title: TitleSizes;
  utility: SerializedStyles;
};

/**
 * A hook that provides typography styles for various text elements.
 *
 * @returns {UseTypography} An object containing typography styles.
 * @property {HeadingSizes} heading - Styles for headings with various predefined sizes.
 * @property {TitleSizes} title - Styles for titles with various predefined sizes.
 * @property {BodySizes} body - Styles for body text with predefined sizes.
 * @property {SerializedStyles} link - Style for links.
 * @property {SerializedStyles} detail - Style for detail text.
 * @property {SerializedStyles} utility - Style for utility text.
 * @property {ParagraphSizes} paragraph - Styles for paragraphs with predefined sizes.
 * @property {ListSizes} list - Styles for lists, following paragraph font sizes.
 */
const useTypography = () => {
  const {font, spacings} = useAppTheme();

  // Heading
  const headingDefault = css({
    lineHeight: '1em',
    fontFamily: font.family.serif,
    fontWeight: font.weight.bold,
  });

  const heading = {
    /** 64px */
    xxl: css([headingDefault, {fontSize: spacings.px64}]),
    /** 56px */
    xl: css([headingDefault, {fontSize: spacings.px56}]),
    /** 44px */
    l: css([headingDefault, {fontSize: spacings.px44}]),
    /** 32px */
    m: css([headingDefault, {fontSize: spacings.px32}]),
    /** 24px */
    s: css([headingDefault, {fontSize: spacings.px24}]),
  } as const satisfies HeadingSizes;

  // Title
  const titleDefault = css({
    fontFamily: font.family.sans,
    fontWeight: font.weight.bold,
  });

  const title = {
    /** 44px */
    xxl: css([titleDefault, {fontSize: spacings.px44, lineHeight: '1.5em'}]),
    /** 32px */
    xl: css([titleDefault, {fontSize: spacings.px32, lineHeight: '1.5em'}]),
    /** 24px */
    l: css([titleDefault, {fontSize: spacings.px24, lineHeight: '1.5em'}]),
    /** 22px */
    m: css([titleDefault, {fontSize: spacings.px22, lineHeight: '1.5em'}]),
    /** 18px */
    s: css([titleDefault, {fontSize: spacings.px18, lineHeight: '1.4em'}]),
    /** 16px */
    xs: css([titleDefault, {fontSize: spacings.px16, lineHeight: '1.4em'}]),
    /** 13px */
    xxs: css([titleDefault, {fontSize: spacings.px13, lineHeight: '1.4em'}]),
  } as const satisfies TitleSizes;

  // Body
  const bodyDefault = css({
    fontFamily: font.family.sans,
    lineHeight: '1.4em',
    fontWeight: font.weight.normal,
  });

  /**
   * l: 18px
   *
   * m: 16px
   *
   * s: 14px
   */
  const body = {
    /** 18px */
    l: css([bodyDefault, {fontSize: spacings.px18}]),
    /** 16px */
    m: css([bodyDefault, {fontSize: spacings.px16}]),
    /** 14px */
    s: css([bodyDefault, {fontSize: spacings.px14}]),
  } as const satisfies BodySizes;

  // Link
  /** 14px */
  const link = css({
    fontFamily: font.family.sans,
    fontSize: spacings.px14,
    lineHeight: '1.4em',
    fontWeight: font.weight.bold,
  });

  // Detail
  /** 11px */
  const detail = css({
    fontFamily: font.family.sans,
    fontSize: spacings.px11,
    lineHeight: '1.4em',
    fontWeight: font.weight.normal,
  });

  // Utility
  /** 9px */
  const utility = css({
    fontFamily: font.family.sans,
    fontSize: spacings.px9,
    lineHeight: '1.4em',
    fontWeight: font.weight.normal,
  });

  // Paragraph
  const paragraphDefault = css({
    fontFamily: font.family.sans,
    fontWeight: font.weight.normal,
    lineHeight: '2em',
  });

  const paragraph = {
    /** 18px */
    l: css([
      paragraphDefault,
      {
        fontSize: spacings.px18,
        '&:not(:last-of-type)': {marginBottom: spacings.px32},
      },
    ]),
    /** 16px */
    m: css([
      paragraphDefault,
      {
        fontSize: spacings.px16,
        '&:not(:last-of-type)': {marginBottom: spacings.px24},
      },
    ]),
    /** 14px */
    s: css([
      paragraphDefault,
      {
        fontSize: spacings.px14,
        '&:not(:last-of-type)': {marginBottom: spacings.px16},
      },
    ]),
    /** 12px */
    xs: css([
      paragraphDefault,
      {
        fontSize: spacings.px12,
        '&:not(:last-of-type)': {marginBottom: spacings.px12},
      },
    ]),
  } as const satisfies ParagraphSizes;

  // List
  const listDefault = css({
    fontFamily: font.family.sans,
    fontWeight: font.weight.normal,
    display: 'flex',
    flexDirection: 'column',
    gap: spacings.px12,
    listStyle: 'inside',
  });

  /** Follows Paragraph font size */
  const list = {
    /** 18px */
    l: css([paragraph.l, listDefault]),
    /** 16px */
    m: css([paragraph.m, listDefault]),
    /** 14px */
    s: css([paragraph.s, listDefault]),
    /** 12px */
    xs: css([paragraph.xs, listDefault]),
  } as const satisfies ListSizes;

  return {
    heading,
    title,
    /**
     * l: 18px
     *
     * m: 16px
     *
     * s: 14px
     */
    body,
    /** 14px */
    link,
    /** 11px */
    detail,
    /** 9px */
    utility,
    paragraph,
    list,
  } as const satisfies UseTypography;
};

export default useTypography;
